import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  updateProductPrice,
  calculateProductPrice,
  saveProductPrice,
  updateChemicalPrice,
  getProductPricesPage,
  bulkDeleteProductPrices,
} from '../../api/priceCalculation';
import styles from './PriceCalculation.module.scss';
import { Edit, X, Save, RefreshCw, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle, Trash2 } from 'lucide-react';

export default function ProductPricesTab({ onError }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ price_per_kg: '' });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [recalcProduct, setRecalcProduct] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async (page, search) => {
    setLoading(true);
    if (onError) onError(null);
    try {
      const skip = (page - 1) * itemsPerPage;
      const data = await getProductPricesPage({
        search: search || '',
        skip,
        limit: itemsPerPage,
      });
      setList(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total ?? 0);
    } catch (e) {
      if (onError) onError(e.message);
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    load(currentPage, debouncedSearch);
  }, [load, currentPage, debouncedSearch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, debouncedSearch]);

  const deletableOnPage = list.filter((row) => row.price_per_kg != null);
  const pageDeletableIds = deletableOnPage.map((row) => row.product_id);
  const allPageSelected =
    pageDeletableIds.length > 0 && pageDeletableIds.every((id) => selectedIds.has(id));

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageDeletableIds.forEach((id) => next.delete(id));
      } else {
        pageDeletableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Delete ${ids.length} product price(s)? History will be kept; current prices will be cleared.`
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    if (onError) onError(null);
    try {
      await bulkDeleteProductPrices(ids);
      setSelectedIds(new Set());
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      price_per_kg: row?.price_per_kg != null ? String(row.price_per_kg) : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editRow) return;
    const price = parseFloat(form.price_per_kg);
    if (isNaN(price) || price < 0) {
      if (onError) onError('Enter a valid price (≥ 0)');
      return;
    }
    setSaving(true);
    if (onError) onError(null);
    try {
      await updateProductPrice(editRow.product_id, {
        price_per_kg: price,
      });
      setEditRow(null);
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openRecalc = async (product) => {
    setRecalcProduct(product);
    setBreakdown(null);
    setOverrides([]);
    setRecalcLoading(true);
    if (onError) onError(null);
    try {
      const data = await calculateProductPrice(product.product_id, null);
      setBreakdown(data);
      setOverrides(
        (data.components || []).map((c) => ({
          chemical_id: c.chemical_id,
          price_per_kg: c.price_per_kg,
        }))
      );
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setRecalcLoading(false);
    }
  };

  const setOverridePrice = (chemicalId, value) => {
    setOverrides((prev) => {
      const next = prev.map((o) =>
        o.chemical_id === chemicalId ? { ...o, price_per_kg: value } : o
      );
      if (!next.some((o) => o.chemical_id === chemicalId)) {
        next.push({ chemical_id: chemicalId, price_per_kg: value });
      }
      return next;
    });
  };

  // Calculate costs locally based on current overrides
  const calculatedBreakdown = useMemo(() => {
    if (!breakdown) return null;
    
    const components = (breakdown.components || []).map((c) => {
      const ov = overrides.find((o) => o.chemical_id === c.chemical_id);
      let price = c.price_per_kg || 0;
      
      // Use override if it's a valid number
      if (ov != null && ov.price_per_kg !== '') {
        const parsed = parseFloat(ov.price_per_kg);
        if (!isNaN(parsed) && parsed >= 0) {
          price = parsed;
        }
      }
      
      const qtyKg = c.quantity_kg || 0;
      const cost = qtyKg * price;
      
      return {
        ...c,
        calculated_price: price,
        calculated_cost: cost,
      };
    });
    
    const totalCost = components.reduce((sum, c) => sum + (c.calculated_cost || 0), 0);
    // Product price per kg = Total Component Cost (sum of all component costs)
    const productPricePerKg = totalCost;
    
    return {
      ...breakdown,
      components,
      calculated_total_cost: totalCost,
      calculated_product_price_per_kg: productPricePerKg,
    };
  }, [breakdown, overrides]);

  const handleRecalculate = async () => {
    if (!recalcProduct) return;
    setRecalcLoading(true);
    if (onError) onError(null);
    try {
      const body = overrides
        .filter((o) => o.price_per_kg !== '' && !isNaN(parseFloat(o.price_per_kg)))
        .map((o) => ({ chemical_id: o.chemical_id, price_per_kg: parseFloat(o.price_per_kg) }));
      const data = await calculateProductPrice(
        recalcProduct.product_id,
        body.length ? body : null
      );
      setBreakdown(data);
      setOverrides(
        (data.components || []).map((c) => ({
          chemical_id: c.chemical_id,
          price_per_kg: c.price_per_kg,
        }))
      );
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setRecalcLoading(false);
    }
  };

  const handleSaveProductPrice = async () => {
    if (!recalcProduct) return;
    
    // Check if there are any price overrides that differ from original
    const hasOverrides = overrides.some((ov) => {
      const original = breakdown?.components?.find((c) => c.chemical_id === ov.chemical_id);
      return original && ov.price_per_kg !== '' && parseFloat(ov.price_per_kg) !== original.price_per_kg;
    });

    if (hasOverrides) {
      // Show confirmation dialog
      setPendingSave({ recalcProduct, overrides, breakdown });
      setShowConfirmDialog(true);
    } else {
      // No overrides, just save product price
      await executeSaveProductPrice(recalcProduct, overrides, breakdown);
    }
  };

  const executeSaveProductPrice = async (product, overridesList) => {
    setSavingProduct(true);
    if (onError) onError(null);
    try {
      const body = overridesList
        .filter((o) => o.price_per_kg !== '' && !isNaN(parseFloat(o.price_per_kg)))
        .map((o) => ({ chemical_id: o.chemical_id, price_per_kg: parseFloat(o.price_per_kg) }));
      await saveProductPrice(product.product_id, body.length ? body : null);
      setRecalcProduct(null);
      setShowConfirmDialog(false);
      setPendingSave(null);
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const handleConfirmSaveWithChemicalUpdates = async (updateChemicals) => {
    if (!pendingSave) return;
    
    setSavingProduct(true);
    if (onError) onError(null);
    try {
      const { recalcProduct: product, overrides: overridesList } = pendingSave;
      
      // Save product price
      const body = overridesList
        .filter((o) => o.price_per_kg !== '' && !isNaN(parseFloat(o.price_per_kg)))
        .map((o) => ({ chemical_id: o.chemical_id, price_per_kg: parseFloat(o.price_per_kg) }));
      await saveProductPrice(product.product_id, body.length ? body : null);
      
      // Update chemical prices if user confirmed
      if (updateChemicals && body.length > 0) {
        const updatePromises = body.map((override) =>
          updateChemicalPrice(override.chemical_id, {
            price_per_kg: override.price_per_kg,
          })
        );
        await Promise.all(updatePromises);
      }
      
      setRecalcProduct(null);
      setShowConfirmDialog(false);
      setPendingSave(null);
      await load(currentPage, debouncedSearch);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);
  const shownFrom = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const shownTo = Math.min(currentPage * itemsPerPage, total);

  return (
    <>
      {/* Search + bulk actions */}
      <div className={styles.toolbar}>
        <div className={styles.searchBar} style={{ marginBottom: 0, flex: 1, maxWidth: 500 }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={18} style={{ position: 'absolute', left: 12, color: 'var(--secondary-text, #6b7280)' }} />
            <input
              type="text"
              placeholder="Search products by name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--secondary-text, #6b7280)',
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {selectedIds.size > 0 && (
          <button
            type="button"
            className={styles.bulkDeleteBtn}
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            title="Delete selected prices"
          >
            <Trash2 size={16} />
            {bulkDeleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className={styles.loading}>Loading product prices…</div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      disabled={pageDeletableIds.length === 0}
                      onChange={toggleSelectPage}
                      title="Select all with prices on this page"
                      aria-label="Select all with prices on this page"
                    />
                  </th>
                  <th>Product</th>
                  <th>Price per kg</th>
                  <th>Source</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.empty}>
                      {debouncedSearch ? `No products found matching "${debouncedSearch}"` : 'No product prices.'}
                    </td>
                  </tr>
                ) : (
                  list.map((row) => {
                    const canDelete = row.price_per_kg != null;
                    return (
                      <tr key={row.product_id}>
                        <td className={styles.checkboxCell}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.product_id)}
                            disabled={!canDelete}
                            onChange={() => toggleRow(row.product_id)}
                            title={canDelete ? 'Select for delete' : 'No price set'}
                            aria-label={`Select ${row.product_name || 'product'}`}
                          />
                        </td>
                        <td>{row.product_name ?? '—'}</td>
                        <td>
                          {row.price_per_kg != null
                            ? Number(row.price_per_kg).toFixed(2)
                            : '—'}
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${
                              row.is_calculated ? styles.calculated : styles.manual
                            }`}
                          >
                            {row.is_calculated ? 'Calculated' : 'Manual'}
                          </span>
                        </td>
                        <td>
                          {row.updated_at
                            ? new Date(row.updated_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={() => openEdit(row)}
                              title="Edit (manual price)"
                            >
                              <Edit size={14} /> Edit
                            </button>
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={() => openRecalc(row)}
                              title="Recalculate from formulation"
                            >
                              <RefreshCw size={14} /> Recalculate
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {shownFrom} to {shownTo} of {total} products
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                
                <div className={styles.pageNumbers}>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          type="button"
                          className={styles.pageNumber}
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis1" style={{ padding: '0 4px' }}>...</span>);
                      }
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          type="button"
                          className={`${styles.pageNumber} ${currentPage === i ? styles.active : ''}`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis2" style={{ padding: '0 4px' }}>...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          type="button"
                          className={styles.pageNumber}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit product price modal */}
      {editRow && (
        <div className={styles.modalOverlay} onClick={() => setEditRow(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Edit product price – {editRow.product_name}</h3>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setEditRow(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Price per kg</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price_per_kg}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price_per_kg: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setEditRow(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveEdit}
                disabled={saving}
              >
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recalculate modal */}
      {recalcProduct && (
        <div
          className={styles.modalOverlay}
          onClick={() => setRecalcProduct(null)}
        >
          <div
            className={styles.modal}
            style={{ maxWidth: '900px', maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>Recalculate – {recalcProduct.product_name}</h3>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setRecalcProduct(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody} style={{ maxHeight: 'calc(85vh - 140px)', overflowY: 'auto' }}>
              {recalcLoading && !breakdown ? (
                <div className={styles.loading}>Loading breakdown…</div>
              ) : calculatedBreakdown ? (
                <>
                  <div style={{ marginBottom: 16, padding: 12, background: 'var(--card-bg)', borderRadius: 8, fontSize: 14, border: '1px solid var(--border-color)', color: 'var(--primary-text)' }}>
                    <strong>Base Composition:</strong> {calculatedBreakdown.base_composition_qty} {calculatedBreakdown.base_composition_unit} ({Number(calculatedBreakdown.base_composition_kg).toFixed(4)} kg)
                  </div>
                  <div className={styles.tableWrap} style={{ marginBottom: 16 }}>
                    <table className={`${styles.table} ${styles.breakdownTable}`}>
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Qty (g)</th>
                          <th>Qty (kg)</th>
                          <th>Price per kg</th>
                          <th>Component Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calculatedBreakdown.components.map((c) => {
                          const ov = overrides.find(
                            (o) => o.chemical_id === c.chemical_id
                          );
                          const cost = c.calculated_cost;
                          // Use more precision for very small costs
                          const costDisplay = cost < 0.01 ? cost.toFixed(6) : cost.toFixed(4);
                          return (
                            <tr key={c.chemical_id}>
                              <td style={{ fontWeight: 500, color: 'var(--primary-text)' }}>{c.chemical_name}</td>
                              <td>{Number(c.quantity_g || 0).toFixed(2)}</td>
                              <td>{(c.quantity_kg || 0).toFixed(6)}</td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={ov?.price_per_kg !== undefined && ov.price_per_kg !== '' ? ov.price_per_kg : (c.price_per_kg || '')}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setOverridePrice(c.chemical_id, val);
                                  }}
                                  onBlur={(e) => {
                                    // Ensure valid number on blur
                                    const val = e.target.value;
                                    if (val !== '' && (isNaN(parseFloat(val)) || parseFloat(val) < 0)) {
                                      setOverridePrice(c.chemical_id, c.price_per_kg || '');
                                    }
                                  }}
                                  style={{ width: 120, padding: '6px 8px', fontSize: 14, background: 'var(--card-bg)', color: 'var(--primary-text)', border: '1px solid var(--border-color)' }}
                                />
                              </td>
                              <td style={{ fontWeight: 500 }}>{costDisplay}</td>
                            </tr>
                          );
                        })}
                        <tr className={styles.totalRow}>
                          <td colSpan={4}><strong>Total Component Cost</strong></td>
                          <td><strong>{calculatedBreakdown.calculated_total_cost.toFixed(4)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div style={{ 
                    padding: 16, 
                    background: 'var(--card-bg)', 
                    borderRadius: 8, 
                    border: '1px solid var(--border-color)',
                    marginTop: 8
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--secondary-text)', marginBottom: 4 }}>Total Component Cost</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--primary-text)' }}>{calculatedBreakdown.calculated_total_cost.toFixed(4)}</div>
                      </div>
                      <div style={{ fontSize: 20, color: 'var(--secondary-text)' }}>=</div>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--secondary-text)', marginBottom: 4 }}>Product Price per kg</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent-color, #2563eb)' }}>
                          {calculatedBreakdown.calculated_product_price_per_kg.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setRecalcProduct(null)}
              >
                Close
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleRecalculate}
                disabled={recalcLoading}
              >
                <RefreshCw size={14} /> Recalculate
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveProductPrice}
                disabled={savingProduct || !breakdown}
              >
                <Save size={14} /> {savingProduct ? 'Saving…' : 'Save as product price'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation dialog for updating chemical prices */}
      {showConfirmDialog && (
        <div className={styles.modalOverlay} onClick={() => { setShowConfirmDialog(false); setPendingSave(null); }}>
          <div className={styles.modal} style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AlertCircle size={24} style={{ color: '#f59e0b' }} />
                <h3>Update Chemical Prices?</h3>
              </div>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => { setShowConfirmDialog(false); setPendingSave(null); }}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ marginBottom: 16 }}>
                You've modified chemical prices in this calculation. Would you like to update these prices in the <strong>Chemical Prices</strong> table as well?
              </p>
              <p style={{ fontSize: 14, color: 'var(--secondary-text)', marginBottom: 0 }}>
                This will update the stored price per kg for these chemicals, which will be used in future calculations.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => handleConfirmSaveWithChemicalUpdates(false)}
                disabled={savingProduct}
              >
                No, save product price only
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => handleConfirmSaveWithChemicalUpdates(true)}
                disabled={savingProduct}
              >
                <Save size={14} /> {savingProduct ? 'Saving…' : 'Yes, update chemical prices'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
